/**
 * MODULE: LangflowAssistantBrainStrip.tsx
 * PURPOSE: Compact context chips + catch-up hints for full-page Langflow assistant.
 * RECENT: 2026-07-08 [2.2.1478] — Learning status banner removed; top-nav BrainLearningsNavBadge owns learnings.
 */

import type { AssistantContextResponse } from '@shared/lib/frontend/api/assistantContextApi';
import { AssistantLearningCatchUpBanner } from '../../../components/assistant/AssistantLearningCatchUpBanner';
import {
  formatAssistantTopicLabel,
  shouldShowAssistantTopicChip,
} from './assistantContextChips';

export type LangflowAssistantBrainStripProps = {
  context: AssistantContextResponse | null;
  learningCatchUpHint: string | null;
  onDismissCatchUp?: () => void;
  pageLabel?: string;
  activeTopicSlug?: string;
  /** True while awaiting stream routing metadata (show subtle pulse on topic chip). */
  topicPreviewPending?: boolean;
  /** Page variant: tighter spacing (learning banner lives in top nav). */
  compact?: boolean;
  /** Dock composer: tighter spacing above the input row. */
  dockEmbedded?: boolean;
  /** Dock header hosts the page pill — omit it from the context rail. */
  hidePageLabel?: boolean;
  /** Dock header hosts the focus chip — omit duplicate from the context rail [2.2.1351]. */
  hideFocusLabel?: boolean;
};

export function LangflowAssistantBrainStrip({
  context,
  learningCatchUpHint,
  onDismissCatchUp,
  pageLabel = '',
  activeTopicSlug = '',
  topicPreviewPending = false,
  compact: _compact = false,
  dockEmbedded = false,
  hidePageLabel = false,
  hideFocusLabel = false,
}: LangflowAssistantBrainStripProps) {
  const routeQueryType = context?.tool_route?.query_type?.trim() ?? '';
  const resolvedTopic =
    activeTopicSlug.trim() ||
    (context?.topic_slug || '').trim() ||
    routeQueryType ||
    (context?.query_type || '').trim();
  const focusLabel = context?.focus_entity?.display_name?.trim();
  const topicLabel = shouldShowAssistantTopicChip(resolvedTopic)
    ? formatAssistantTopicLabel(resolvedTopic)
    : '';
  const showPageLabel = !hidePageLabel && Boolean(pageLabel.trim());
  const showFocusInRail = Boolean(focusLabel) && !hideFocusLabel;
  const showContextRail = showPageLabel || Boolean(topicLabel) || showFocusInRail;

  if (!learningCatchUpHint && !showContextRail) {
    return null;
  }

  return (
    <div
      className={
        dockEmbedded
          ? 'flex flex-col gap-1 border-b border-gray-100 px-2 py-1 dark:border-gray-700'
          : 'mb-2 flex flex-col gap-2'
      }
      data-testid="langflow-assistant-brain-strip"
    >
      {showContextRail ? (
        <div
          className="flex min-w-0 flex-wrap items-center gap-1.5"
          data-testid="langflow-assistant-context-rail"
          data-topic-slug={resolvedTopic || undefined}
        >
          {showPageLabel ? (
            <span className="inline-flex max-w-[14rem] items-center gap-1 truncate rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
              <span className="text-gray-400 dark:text-gray-500" aria-hidden>
                ◆
              </span>
              <span className="truncate">{pageLabel.trim()}</span>
            </span>
          ) : null}
          {topicLabel ? (
            <span
              className={`inline-flex max-w-[12rem] items-center gap-1 truncate rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-[11px] font-medium text-primary-800 dark:border-primary-800 dark:bg-primary-950/50 dark:text-primary-200${
                topicPreviewPending ? ' animate-pulse opacity-90' : ''
              }`}
            >
              <span aria-hidden>◎</span>
              <span className="truncate">{topicLabel}</span>
              <span className="sr-only">{resolvedTopic}</span>
            </span>
          ) : null}
          {showFocusInRail ? (
            <span className="inline-flex max-w-[12rem] truncate rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-200">
              {focusLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {learningCatchUpHint ? (
        <AssistantLearningCatchUpBanner
          hint={learningCatchUpHint}
          compact
          onDismiss={onDismissCatchUp}
        />
      ) : null}
    </div>
  );
}
